import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from './utils/supabaseClient';
import {
    Box,
    Button,
    Center,
    Container,
    VStack,
    HStack,
    Heading,
    Text,
    Spinner,
    Alert,
    AlertIcon,
    Badge,
    Divider,
    useToast,
    Icon,
} from '@chakra-ui/react';
import { LockIcon } from '@chakra-ui/icons';
import moment from 'moment';

/**
 * Compute the next registration stage after the registration fee is paid,
 * mirroring the fixed 8-step sequence in enrollmentPolicy.js.
 */
function nextStageAfterRegFee(policySnapshot) {
    const snap = policySnapshot ?? {};
    if (snap.needs_approval) return 'pending_approval';
    if (snap.requires_documents) return 'documents_pending';
    const ct = snap.commitment_type || 'full';
    if (ct !== 'interest_registration' && ct !== 'none') return 'payment_pending';
    return 'enrolled';
}

export const RegistrationFeePayment = ({ registrationId: propId } = {}) => {
    const params = useParams();
    const registrationId = propId || params.id || params.registrationID;
    const toast = useToast();

    const [registration, setRegistration] = useState(null);
    const [runDisplay, setRunDisplay] = useState(null);
    const [invoiceId, setInvoiceId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [awaitingWebhook, setAwaitingWebhook] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [orgGstSettings, setOrgGstSettings] = useState({ gst_enabled: false, gst_rate: 0 });
    const advancingRef = useRef(false);

    /**
     * Advance the registration stage from registration_fee_pending to the next
     * step in the fixed sequence, then hard-reload so RegistrationHub re-evaluates.
     */
    const advanceStage = async (policySnapshot) => {
        if (advancingRef.current) return;
        advancingRef.current = true;
        const next = nextStageAfterRegFee(policySnapshot);
        console.log(`[RegistrationFeePayment] Fee verified as paid. Advancing stage → ${next}`);

        const updateData = {
            registration_stage: next,
            stage_updated_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };

        await supabase
            .from('registration')
            .update(updateData)
            .eq('id', registrationId);
        window.location.href = `/register/${registrationId}`;
    };

    /**
     * Query the payments table (ground truth) to check whether the registration
     * fee has actually been settled.
     *
     * Business rule: SUM(payments.amount WHERE invoice_id IN (reg-fee invoices)
     *                AND status = 'succeeded') >= policy.registration_fee_amount
     *
     * This prevents stage-column hacking — the payments table is only written to
     * by the Stripe webhook (service-role key) and is not editable by participants.
     */
    const checkPaymentBalance = async (invId, feeAmount, policySnapshot) => {
        if (!invId || feeAmount <= 0) return false;
        const { data: pmts } = await supabase
            .from('payments')
            .select('amount')
            .eq('invoice_id', invId)
            .eq('status', 'succeeded');
        const totalPaid = (pmts ?? []).reduce((sum, p) => sum + Number(p.amount), 0);
        console.log(`[RegistrationFeePayment] totalPaid=${totalPaid}, feeRequired=${feeAmount}`);
        if (totalPaid >= feeAmount) {
            await advanceStage(policySnapshot);
            return true;
        }
        return false;
    };

    /**
     * Ensure a registration-fee invoice row exists in the database.
     * Creates the invoice + line item if absent so that:
     *   - TMS can see the outstanding invoice immediately
     *   - The balance check (SUM invoices − SUM payments) works before checkout
     */
    const ensureInvoice = async (registrationData) => {
        const feeAmount = Number(registrationData?.policy_snapshot?.registration_fee_amount ?? 0);
        if (!Number.isFinite(feeAmount) || feeAmount <= 0) return null;

        const { data: existing } = await supabase
            .from('invoices')
            .select('id, status, amount_paid')
            .eq('registration_id', registrationId)
            .eq('invoice_type', 'registration fee');

        if (existing?.length > 0) {
            return existing[0].id;
        }

        const { data: invId, error: rpcErr } = await supabase.rpc('ensure_registration_fee_invoice', {
            p_registration_id: registrationId,
        });

        if (rpcErr) {
            console.error('[RegistrationFeePayment] ensure_registration_fee_invoice RPC failed:', rpcErr.message);
            return null;
        }

        return invId || null;
    };

    // ─── Bootstrap: fetch data, ensure invoice, verify payment state ────────────
    useEffect(() => {
        if (!registrationId) return;

        const bootstrap = async () => {
            setLoading(true);
            setFetchError(null);
            try {
                // 1. Fetch registration
                const { data: reg, error: regErr } = await supabase
                    .from('registration')
                    .select('*')
                    .eq('id', registrationId)
                    .single();
                if (regErr) throw regErr;
                setRegistration(reg);

                // 2. Fetch course or certification run for display
                if (reg.draft_cert_run_code) {
                    const { data: certRun } = await supabase
                        .from('full_certification_runs')
                        .select('id, start_date, end_date, full_cert_type_code, full_certification:full_cert_type_code(name, code)')
                        .eq('id', reg.draft_cert_run_code)
                        .single();
                    if (certRun) {
                        setRunDisplay({
                            kind: 'certification',
                            name: certRun.full_certification?.name || certRun.full_cert_type_code || 'Programme',
                            start_date: certRun.start_date,
                            end_date: certRun.end_date,
                            venue: null,
                        });
                    }
                } else if (reg.draft_course_run_code) {
                    const { data: cr } = await supabase
                        .from('course_run')
                        .select('id, start_date, end_date, venue, course_type_code, course_type:course_type_code(name, code)')
                        .eq('id', reg.draft_course_run_code)
                        .single();
                    if (cr) {
                        setRunDisplay({
                            kind: 'course',
                            name: cr.course_type?.name || cr.course_type_code || 'Course',
                            start_date: cr.start_date,
                            end_date: cr.end_date,
                            venue: cr.venue,
                        });
                    }
                }

                // 3. Fetch organization GST settings
                let gstSettings = { gst_enabled: false, gst_rate: 0 };
                try {
                    const { data: orgData, error: orgErr } = await supabase
                        .from('organization')
                        .select('gst_enabled, gst_rate')
                        .eq('status', 'active')
                        .single();
                    if (!orgErr && orgData) {
                        gstSettings = {
                            gst_enabled: orgData.gst_enabled || false,
                            gst_rate: parseFloat(orgData.gst_rate) || 0,
                        };
                    }
                } catch (e) {
                    console.error('[RegistrationFeePayment] Error fetching org GST settings:', e);
                }
                setOrgGstSettings(gstSettings);

                const feeAmount = Number(reg?.policy_snapshot?.registration_fee_amount ?? 0);

                // 4. Ensure the invoice exists in the DB (with GST fields)
                const invId = await ensureInvoice(reg);
                setInvoiceId(invId);

                // 5. Verify via payments table — if already fully paid, advance stage
                if (invId) {
                    const alreadyPaid = await checkPaymentBalance(invId, feeAmount, reg.policy_snapshot);
                    if (alreadyPaid) return;
                }
            } catch (err) {
                setFetchError(err.message || 'Failed to load payment details.');
            } finally {
                setLoading(false);
            }
        };

        bootstrap();
    }, [registrationId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Payment Required email (template_key: payment_required) ─────────────────
    // Sent once when the fee payment screen loads; edge function + RPC enforce idempotency.
    useEffect(() => {
        if (!registrationId || loading || fetchError || !registration) return;
        if (registration.registration_stage !== 'registration_fee_pending') return;
        const feeAmount = Number(registration.policy_snapshot?.registration_fee_amount ?? 0);
        if (!Number.isFinite(feeAmount) || feeAmount <= 0) return;

        supabase.functions
            .invoke('send-payment-required-email', { body: { registrationId } })
            .then(({ error }) => {
                if (error) console.error('[RegistrationFeePayment] send-payment-required-email:', error);
            });
    }, [registrationId, loading, fetchError, registration]);

    // ─── Detect return from Stripe ───────────────────────────────────────────────
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const paymentParam = searchParams.get('payment');
        if (paymentParam === 'success') {
            setAwaitingWebhook(true);
            window.history.replaceState({}, '', `/register/${registrationId}`);
        } else if (paymentParam === 'cancelled') {
            toast({
                title: 'Payment cancelled',
                description: 'Your payment was not completed. You can try again when ready.',
                status: 'warning',
                duration: 6000,
                isClosable: true,
            });
            window.history.replaceState({}, '', `/register/${registrationId}`);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Poll for webhook after Stripe redirect ───────────────────────────────────
    useEffect(() => {
        if (!awaitingWebhook || !registrationId || loading) return;

        const interval = setInterval(async () => {
            // Check payments table as the authoritative source
            const feeAmount = Number(registration?.policy_snapshot?.registration_fee_amount ?? 0);
            if (invoiceId) {
                const paid = await checkPaymentBalance(invoiceId, feeAmount, registration?.policy_snapshot);
                if (paid) {
                    clearInterval(interval);
                    return;
                }
            }
            // Fallback: check stage changed
            const { data } = await supabase
                .from('registration')
                .select('registration_stage')
                .eq('id', registrationId)
                .single();
            if (data?.registration_stage && data.registration_stage !== 'registration_fee_pending') {
                clearInterval(interval);
                window.location.href = `/register/${registrationId}`;
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [awaitingWebhook, registrationId, loading, invoiceId, registration]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Start Stripe checkout ────────────────────────────────────────────────────
    const handlePay = async () => {
        if (!registration) return;
        setPaying(true);
        try {
            const feeAmount = Number(registration.policy_snapshot?.registration_fee_amount ?? 0);
            const gstRateDecimal = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
            const feeWithGst = Math.round(feeAmount * (1 + gstRateDecimal) * 100) / 100;

            const courseName = runDisplay?.name ?? 'Course';
            const startDate = runDisplay?.start_date
                ? moment(runDisplay.start_date).format('DD MMM YYYY')
                : null;

            const { data, error } = await supabase.functions.invoke('create-registration-checkout', {
                body: {
                    registration_id: registrationId,
                    participant_email: registration.participant_email,
                    participant_name: registration.participant_name,
                    invoice_id: invoiceId,
                    invoice_balance: feeWithGst,
                    gst_enabled: orgGstSettings.gst_enabled,
                    gst_rate: orgGstSettings.gst_rate,
                    invoice_type: 'registration fee',
                    course_name: courseName,
                    course_start_date: startDate,
                    success_url: `${window.location.origin}/register/${registrationId}?payment=success`,
                    cancel_url: `${window.location.origin}/register/${registrationId}?payment=cancelled`,
                },
            });

            if (error) throw new Error(error.message || 'Failed to start checkout.');
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error('No checkout URL returned from payment service.');
            }
        } catch (err) {
            toast({
                title: 'Unable to start payment',
                description: err.message,
                status: 'error',
                duration: 8000,
                isClosable: true,
            });
            setPaying(false);
        }
    };

    // ─── Render states ────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Center minH="60vh">
                <VStack spacing={3}>
                    <Spinner size="xl" color="orange.400" thickness="4px" />
                    <Text color="gray.500">Loading payment details...</Text>
                </VStack>
            </Center>
        );
    }

    if (fetchError) {
        return (
            <Center minH="60vh">
                <Alert status="error" maxW="500px" borderRadius="md">
                    <AlertIcon />
                    {fetchError}
                </Alert>
            </Center>
        );
    }

    if (awaitingWebhook) {
        return (
            <Center minH="60vh">
                <VStack spacing={4}>
                    <Spinner size="xl" color="green.400" thickness="4px" />
                    <Heading size="md" color="gray.700">Confirming your payment…</Heading>
                    <Text color="gray.500" textAlign="center" maxW="360px">
                        Please wait while we verify your payment. This usually takes a few seconds.
                    </Text>
                </VStack>
            </Center>
        );
    }

    const feeAmount = Number(registration?.policy_snapshot?.registration_fee_amount ?? 0);
    const gstRateDecimal = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
    const gstAmount = Math.round(feeAmount * gstRateDecimal * 100) / 100;
    const totalPayable = Math.round((feeAmount + gstAmount) * 100) / 100;

    const courseName = runDisplay?.name ?? 'Course';
    const startDate = runDisplay?.start_date ? moment(runDisplay.start_date).format('DD MMM YYYY') : '–';
    const endDate = runDisplay?.end_date ? moment(runDisplay.end_date).format('DD MMM YYYY') : '–';
    const runLabel = runDisplay?.kind === 'certification' ? 'Programme' : 'Course';

    return (
        <Box minH="60vh" bg="gray.50" py={{ base: 8, md: 10, lg: 12 }}>
            <Container
                maxW={{ base: '100%', md: 'xl', lg: '2xl', '2xl': '3xl' }}
                px={{ base: 4, sm: 6, lg: 8 }}
                centerContent
            >
                <VStack spacing={6} align="stretch" w="100%">
                    <Box
                        bg="white"
                        borderRadius="xl"
                        p={{ base: 6, md: 8, lg: 10 }}
                        boxShadow="md"
                        w="100%"
                    >
                        <VStack spacing={5} align="stretch">
                            {/* Header */}
                            <VStack spacing={2} align="center">
                                <Badge colorScheme="orange" px={3} py={1} borderRadius="full" fontSize="xs">
                                    Registration Fee Required
                                </Badge>
                                <Heading size="lg" textAlign="center" color="gray.800">
                                    Complete Your Registration
                                </Heading>
                                <Text color="gray.500" textAlign="center" fontSize="sm">
                                    A registration fee is required before your application can be reviewed.
                                </Text>
                            </VStack>

                            <Divider />

                            {/* Registration details */}
                            <VStack spacing={3} align="stretch">
                                <HStack justify="space-between">
                                    <Text fontWeight="semibold" color="gray.600" fontSize="sm">{runLabel}</Text>
                                    <Text color="gray.800" textAlign="right" maxW="60%" fontSize="sm">
                                        {courseName}
                                    </Text>
                                </HStack>
                                <HStack justify="space-between">
                                    <Text fontWeight="semibold" color="gray.600" fontSize="sm">Dates</Text>
                                    <Text color="gray.800" fontSize="sm">{startDate} – {endDate}</Text>
                                </HStack>
                                {runDisplay?.venue && (
                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.600" fontSize="sm">Venue</Text>
                                        <Text color="gray.800" fontSize="sm">{runDisplay.venue}</Text>
                                    </HStack>
                                )}
                                <HStack justify="space-between">
                                    <Text fontWeight="semibold" color="gray.600" fontSize="sm">Participant</Text>
                                    <Text color="gray.800" fontSize="sm">{registration?.participant_name}</Text>
                                </HStack>
                            </VStack>

                            <Divider />

                            {/* Fee callout */}
                            <Box bg="orange.50" borderRadius="lg" p={5}>
                                <VStack spacing={2}>
                                    <Text fontSize="sm" color="orange.700" fontWeight="medium">
                                        Registration Fee
                                    </Text>
                                    <HStack justify="space-between" w="100%">
                                        <Text fontSize="sm" color="gray.600">Subtotal</Text>
                                        <Text fontSize="sm" color="gray.800" fontWeight="medium">
                                            SGD {feeAmount.toFixed(2)}
                                        </Text>
                                    </HStack>
                                    {orgGstSettings.gst_enabled && (
                                        <HStack justify="space-between" w="100%">
                                            <Text fontSize="sm" color="gray.600">GST ({orgGstSettings.gst_rate}%)</Text>
                                            <Text fontSize="sm" color="gray.800" fontWeight="medium">
                                                SGD {gstAmount.toFixed(2)}
                                            </Text>
                                        </HStack>
                                    )}
                                    <Divider borderColor="orange.200" />
                                    <HStack justify="space-between" w="100%">
                                        <Text fontSize="md" color="gray.800" fontWeight="bold">
                                            Total{orgGstSettings.gst_enabled ? ` (incl. ${orgGstSettings.gst_rate}% GST)` : ''}
                                        </Text>
                                        <Text fontSize="2xl" fontWeight="bold" color="gray.800" letterSpacing="tight">
                                            SGD {totalPayable.toFixed(2)}
                                        </Text>
                                    </HStack>
                                </VStack>
                            </Box>

                            {/* Pay button */}
                            <Button
                                colorScheme="orange"
                                size="lg"
                                onClick={handlePay}
                                isLoading={paying}
                                loadingText="Redirecting to payment…"
                                w="100%"
                                h="52px"
                                fontSize="md"
                            >
                                Pay SGD {totalPayable.toFixed(2)} Now
                            </Button>

                            {/* Security note */}
                            <HStack justify="center" spacing={2}>
                                <Icon as={LockIcon} color="gray.400" boxSize={3} />
                                <Text fontSize="xs" color="gray.400">
                                    Secured by Stripe · Your payment details are encrypted.
                                </Text>
                            </HStack>
                        </VStack>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
};

export default RegistrationFeePayment;
